import { useState, useEffect, useCallback } from "react";
import { useQueryClient } from "@tanstack/react-query";
import { Search, X, Camera, Check, ChevronsUpDown } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Card, CardContent } from "@/components/ui/card";
import { Command, CommandEmpty, CommandGroup, CommandInput, CommandItem, CommandList } from "@/components/ui/command";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import { cn } from "@/lib/utils";
import { eventQueryKeys } from "@/events/hooks/useEvents";
import { EventService } from "@/events/services/eventService";
import EventCard from "./event-card";
import type { Event } from "@/types/schema";

interface EventsGridProps {
    className?: string;
}

export default function EventsGrid({ className = "" }: EventsGridProps) {
    const queryClient = useQueryClient();

    // Estados de filtros (apenas para o formulário)
    const [searchTerm, setSearchTerm] = useState("");
    const [categoryFilter, setCategoryFilter] = useState<string>("");
    const [locationFilter, setLocationFilter] = useState<string>("");

    // Estados para os comboboxes
    const [categoryOpen, setCategoryOpen] = useState(false);
    const [locationOpen, setLocationOpen] = useState(false);

    // Estados da grid
    const [events, setEvents] = useState<Event[]>([]);
    const [isLoading, setIsLoading] = useState(false);
    const [error, setError] = useState<string | null>(null);
    const [page, setPage] = useState(1);
    const [hasMore, setHasMore] = useState(true);
    const [isLoadingMore, setIsLoadingMore] = useState(false);

    // Função para executar pesquisa
    const executeSearch = useCallback(async (resetPage = true) => {

        if (resetPage) {
            setIsLoading(true);
            setPage(1);
            setEvents([]);
        } else {
            setIsLoadingMore(true);
        }
        setError(null);

        try {
            // Limpar cache
            queryClient.invalidateQueries({ queryKey: eventQueryKeys.all });

            // Construir filtros - enviar apenas os que têm valor
            const filters: any = {
                page: resetPage ? 1 : page,
                limit: 12, // 12 eventos por página
                status: 'Approved',
            };

            // Adicionar filtros apenas se não forem vazios
            if (searchTerm && searchTerm.trim()) {
                filters.searchTerm = searchTerm.trim();
            }
            if (categoryFilter && categoryFilter.trim()) {
                filters.category = categoryFilter.trim();
            }
            if (locationFilter && locationFilter.trim()) {
                filters.location = locationFilter.trim();
            }



            // Fazer requisição diretamente
            const eventsData = await EventService.getAll(filters);



            if (resetPage) {
                setEvents(eventsData?.events || []);
            } else {
                setEvents(prev => [...prev, ...(eventsData?.events || [])]);
            }

            // Verificar se há mais páginas
            const totalEvents = eventsData?.total || 0;
            const currentPage = resetPage ? 1 : page;
            const totalPages = Math.ceil(totalEvents / 12);
            setHasMore(currentPage < totalPages);

            if (!resetPage) {
                setPage(prev => prev + 1);
            }
        } catch (err: any) {
            console.error('❌ [EventsGrid] Erro na pesquisa:', err);
            setError(err.message || 'Erro ao buscar eventos');
        } finally {
            setIsLoading(false);
            setIsLoadingMore(false);
        }
    }, [searchTerm, categoryFilter, locationFilter, page, queryClient]);

    // Função para carregar mais eventos
    const loadMoreEvents = useCallback(() => {
        if (!isLoadingMore && hasMore && !isLoading) {
            executeSearch(false);
        }
    }, [isLoadingMore, hasMore, isLoading, executeSearch]);

    // Função para executar nova pesquisa
    const handleSearch = useCallback(() => {
        executeSearch(true);
    }, [executeSearch]);

    // Carregar eventos iniciais
    useEffect(() => {
        executeSearch();
    }, []); // Apenas no mount

    // Obter localizações e categorias únicas (para os comboboxes)
    const [uniqueLocations, setUniqueLocations] = useState<string[]>([]);
    const [uniqueCategories, setUniqueCategories] = useState<string[]>([]);

    useEffect(() => {
        const loadData = async () => {
            try {
                const data = await EventService.getAll({ page: 1, limit: 1000 });

                // Extrair localizações únicas
                const locations = data.events?.map(event => event.location).filter((loc): loc is string => Boolean(loc)) || [];
                setUniqueLocations([...new Set(locations)]);

                // Extrair categorias únicas (usar campo category em vez de tags)
                const categories = data.events?.map(event => event.category).filter((cat): cat is string => Boolean(cat)) || [];
                setUniqueCategories([...new Set(categories)]);
            } catch (err) {
                console.error('Erro ao carregar dados:', err);
            }
        };
        loadData();
    }, []);

    return (
        <div className={className}>
            {/* Filtros */}
            <section className="py-4 bg-gray-50">
                <div className="container mx-auto px-4">
                    <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
                        {/* Busca por Título */}
                        <div className="lg:col-span-2">
                            <label className="text-sm font-medium mb-2 block">Título do Evento</label>
                            <div className="relative">
                                <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 h-4 w-4" />
                                <Input
                                    placeholder="Digite o título do evento..."
                                    value={searchTerm}
                                    onChange={(e) => setSearchTerm(e.target.value)}
                                    className="pl-10 pr-10"
                                />
                                {searchTerm && (
                                    <Button
                                        variant="ghost"
                                        size="sm"
                                        onClick={() => setSearchTerm("")}
                                        className="absolute right-1 top-1/2 transform -translate-y-1/2 h-8 w-8 p-0"
                                    >
                                        <X className="h-4 w-4" />
                                    </Button>
                                )}
                            </div>
                        </div>

                        {/* Categoria com Busca */}
                        <div>
                            <label className="text-sm font-medium mb-2 block">Categoria</label>
                            <Popover open={categoryOpen} onOpenChange={setCategoryOpen}>
                                <PopoverTrigger asChild>
                                    <Button
                                        variant="outline"
                                        role="combobox"
                                        aria-expanded={categoryOpen}
                                        className="w-full justify-between"
                                    >
                                        {categoryFilter ? categoryFilter : "Selecione uma categoria..."}
                                        <ChevronsUpDown className="ml-2 h-4 w-4 shrink-0 opacity-50" />
                                    </Button>
                                </PopoverTrigger>
                                <PopoverContent className="w-full p-0">
                                    <Command>
                                        <CommandInput placeholder="Buscar categoria..." />
                                        <CommandList>
                                            <CommandEmpty>Nenhuma categoria encontrada.</CommandEmpty>
                                            <CommandGroup>
                                                <CommandItem
                                                    value=""
                                                    onSelect={() => {
                                                        setCategoryFilter("");
                                                        setCategoryOpen(false);
                                                    }}
                                                >
                                                    <Check
                                                        className={cn(
                                                            "mr-2 h-4 w-4",
                                                            categoryFilter === "" ? "opacity-100" : "opacity-0"
                                                        )}
                                                    />
                                                    Todas as categorias
                                                </CommandItem>
                                                {uniqueCategories.map((category) => (
                                                    <CommandItem
                                                        key={category}
                                                        value={category}
                                                        onSelect={() => {
                                                            setCategoryFilter(category);
                                                            setCategoryOpen(false);
                                                        }}
                                                    >
                                                        <Check
                                                            className={cn(
                                                                "mr-2 h-4 w-4",
                                                                categoryFilter === category ? "opacity-100" : "opacity-0"
                                                            )}
                                                        />
                                                        {category}
                                                    </CommandItem>
                                                ))}
                                            </CommandGroup>
                                        </CommandList>
                                    </Command>
                                </PopoverContent>
                            </Popover>
                        </div>

                        {/* Localização com Busca */}
                        <div>
                            <label className="text-sm font-medium mb-2 block">Localização</label>
                            <Popover open={locationOpen} onOpenChange={setLocationOpen}>
                                <PopoverTrigger asChild>
                                    <Button
                                        variant="outline"
                                        role="combobox"
                                        aria-expanded={locationOpen}
                                        className="w-full justify-between"
                                    >
                                        {locationFilter ? locationFilter : "Selecione uma localização..."}
                                        <ChevronsUpDown className="ml-2 h-4 w-4 shrink-0 opacity-50" />
                                    </Button>
                                </PopoverTrigger>
                                <PopoverContent className="w-full p-0">
                                    <Command>
                                        <CommandInput placeholder="Buscar localização..." />
                                        <CommandList>
                                            <CommandEmpty>Nenhuma localização encontrada.</CommandEmpty>
                                            <CommandGroup>
                                                <CommandItem
                                                    value=""
                                                    onSelect={() => {
                                                        setLocationFilter("");
                                                        setLocationOpen(false);
                                                    }}
                                                >
                                                    <Check
                                                        className={cn(
                                                            "mr-2 h-4 w-4",
                                                            locationFilter === "" ? "opacity-100" : "opacity-0"
                                                        )}
                                                    />
                                                    Todas as localizações
                                                </CommandItem>
                                                {uniqueLocations.map((location) => (
                                                    <CommandItem
                                                        key={location}
                                                        value={location}
                                                        onSelect={() => {
                                                            setLocationFilter(location);
                                                            setLocationOpen(false);
                                                        }}
                                                    >
                                                        <Check
                                                            className={cn(
                                                                "mr-2 h-4 w-4",
                                                                locationFilter === location ? "opacity-100" : "opacity-0"
                                                            )}
                                                        />
                                                        {location}
                                                    </CommandItem>
                                                ))}
                                            </CommandGroup>
                                        </CommandList>
                                    </Command>
                                </PopoverContent>
                            </Popover>
                        </div>
                    </div>

                    {/* Botões de Ação */}
                    <div className="flex gap-2 mt-6">
                        <Button
                            variant="outline"
                            onClick={() => {
                                setSearchTerm("");
                                setCategoryFilter("");
                                setLocationFilter("");
                                setPage(1);
                                setEvents([]);
                                setHasMore(true);
                            }}
                            className="flex items-center gap-2"
                        >
                            <X className="h-4 w-4" />
                            Limpar Filtros
                        </Button>
                        <Button
                            onClick={handleSearch}
                            className="flex items-center gap-2"
                        >
                            <Search className="h-4 w-4" />
                            Pesquisar
                        </Button>
                    </div>
                </div>
            </section>

            {/* Lista de Eventos */}
            <section className="py-8">
                <div className="container mx-auto px-4">
                    {isLoading ? (
                        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-6">
                            {Array.from({ length: 12 }).map((_, index) => (
                                <Card key={index} className="animate-pulse overflow-hidden">
                                    <div className="h-32 bg-gradient-to-br from-gray-200 to-gray-300"></div>
                                    <CardContent className="p-5">
                                        <div className="h-4 bg-gray-200 rounded mb-2"></div>
                                        <div className="h-3 bg-gray-200 rounded mb-3 w-2/3"></div>
                                        <div className="space-y-2 mb-4">
                                            <div className="h-3 bg-gray-200 rounded w-1/2"></div>
                                            <div className="h-3 bg-gray-200 rounded w-3/4"></div>
                                        </div>
                                        <div className="flex gap-2">
                                            <div className="h-8 bg-gray-200 rounded flex-1"></div>
                                            <div className="h-8 bg-gray-200 rounded flex-1"></div>
                                        </div>
                                    </CardContent>
                                </Card>
                            ))}
                        </div>
                    ) : error ? (
                        <div className="text-center py-12">
                            <Camera className="h-16 w-16 text-red-500 mx-auto mb-4" />
                            <h3 className="text-xl font-semibold mb-2 text-red-600">Erro ao carregar eventos</h3>
                            <p className="text-red-500 mb-4">{error}</p>
                            <Button onClick={() => executeSearch(true)} variant="outline">
                                Tentar novamente
                            </Button>
                        </div>
                    ) : events.length === 0 ? (
                        <div className="text-center py-12">
                            <Camera className="h-16 w-16 text-muted-foreground mx-auto mb-4" />
                            <h3 className="text-xl font-semibold mb-2">Nenhum evento encontrado</h3>
                            <p className="text-muted-foreground mb-4">
                                Tente ajustar os filtros de busca para encontrar mais eventos
                            </p>
                            <Button
                                variant="outline"
                                onClick={() => {
                                    setSearchTerm("");
                                    setCategoryFilter("");
                                    setLocationFilter("");
                                    executeSearch(true);
                                }}
                                className="flex items-center gap-2"
                            >
                                <X className="h-4 w-4" />
                                Limpar Filtros
                            </Button>
                        </div>
                    ) : (
                        <>
                            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-6">
                                {events.map((event) => (
                                    <EventCard key={event.id} event={event} />
                                ))}
                            </div>

                            {/* Botão Carregar Mais */}
                            {hasMore && (
                                <div className="text-center mt-8">
                                    <Button
                                        onClick={loadMoreEvents}
                                        disabled={isLoadingMore}
                                        variant="outline"
                                        className="min-w-[200px]"
                                    >
                                        {isLoadingMore ? (
                                            <>
                                                <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-gray-900 mr-2"></div>
                                                Carregando...
                                            </>
                                        ) : (
                                            "Carregar Mais"
                                        )}
                                    </Button>
                                </div>
                            )}

                            {!hasMore && events.length > 0 && (
                                <div className="text-center mt-8">
                                    <p className="text-sm text-muted-foreground">
                                        Todos os eventos foram carregados ({events.length} eventos encontrados)
                                    </p>
                                </div>
                            )}
                        </>
                    )}
                </div>
            </section>
        </div>
    );
}
